import React, { useMemo, useState } from 'react';
import './AssetStatusWidget.css';
import {
  ASSET_STATUS_WIDGET_TYPE,
  findDatastreamMeta,
  formatAssetStatusValue,
  getValueRuleMatch,
  readDatastreamValue,
  resolveStatusVisualState,
} from './assetStatusUtils';

const isActionArea = (target) => {
  if (!target || !(target instanceof Element)) return false;
  return Boolean(target.closest('button,a,input,select,textarea,[role="button"],[data-no-drag],.widget-buttons-container'));
};

const buildDragData = (event, id) => {
  const widgetWrapper = event.target.closest('.widget');
  const widgetId = id || (widgetWrapper ? widgetWrapper.id : '');
  const rect = event.currentTarget.getBoundingClientRect();
  const offsetX = event.clientX - rect.left;
  const offsetY = event.clientY - rect.top;
  return `${ASSET_STATUS_WIDGET_TYPE};${widgetId};${offsetX};${offsetY};${widgetId.startsWith('base') ? 'add' : 'move'}`;
};

const AssetStatusIcon = ({ color, animation }) => (
  <span
    className={[
      'asset-status-widget__icon',
      animation && animation !== 'none' ? `asset-status-widget__anim-${animation}` : '',
    ].filter(Boolean).join(' ')}
    style={{ '--asset-status-active-color': color }}
    aria-hidden="true"
  >
    <span className="asset-status-widget__iconCore" />
  </span>
);

const AssetStatusWidget = ({
  id,
  title,
  transparentBackground,
  draggable,
  datastreams,
  decimals,
  dims,
  assetName = 'Name of Assets',
  rows = [],
  iconDataId,
  iconPin,
  iconGradientEnabled = false,
  iconGradientDataId,
  iconGradientPin,
  iconGradientColors = [],
  iconGradientColorGrading = true,
  iconRules = [],
  iconColor = '#f31f28',
  iconDefaultAnimation = 'pulse',
  borderEnabled = false,
  borderWidth = 1,
  labelFontSize,
  titleFontSize,
  assetNameFontSize,
  datastreamLabelFontSize,
  valueFontSize,
}) => {
  const [cursorState, setCursorState] = useState('idle');
  const list = useMemo(() => (Array.isArray(datastreams) ? datastreams : []), [datastreams]);

  const normalizedRows = useMemo(() => {
    const configured = Array.isArray(rows) ? rows : [];
    const sourceRows = configured.length > 0
      ? configured
      : list.slice(0, 3).map((ds, index) => ({
        dataId: ds?.id,
        pin: ds?.pin,
        label: index === 0 ? 'Status' : 'Text',
      }));

    return sourceRows.slice(0, 6).map((row, index) => {
      const ds = findDatastreamMeta(list, row?.dataId, row?.pin);
      const value = readDatastreamValue(ds);
      const valueText = ds
        ? formatAssetStatusValue(value, row?.decimals ?? ds?.decimals ?? decimals)
        : '--';
      const unit = row?.showUnit === false ? '' : (row?.unit ?? ds?.unit ?? '');
      return {
        key: `${row?.dataId || row?.pin || index}-${index}`,
        dataId: row?.dataId ?? ds?.id ?? null,
        pin: row?.pin ?? ds?.pin ?? '',
        datastream: ds,
        rawValue: value,
        label: ds?.name || row?.label || (index === 0 ? 'Status' : 'Text'),
        valueText: `${valueText}${unit ? ` ${unit}` : ''}`,
      };
    });
  }, [rows, list, decimals]);

  const findRowDatastream = (dataId, pin) => {
    const matchedRow = normalizedRows.find((row) => {
      if (dataId !== undefined && dataId !== null && Number(dataId) > 0) {
        return Number(row?.dataId) === Number(dataId);
      }
      if (pin !== undefined && pin !== null && String(pin).trim() !== '') {
        return String(row?.pin || '') === String(pin);
      }
      return false;
    });

    return matchedRow?.datastream || null;
  };

  const fallbackIconDatastream = normalizedRows.find((row) => row.datastream)?.datastream || null;
  const iconDatastream =
    findRowDatastream(iconDataId, iconPin) ||
    findRowDatastream(iconGradientDataId, iconGradientPin) ||
    fallbackIconDatastream;
  const iconDatastreamValue = readDatastreamValue(iconDatastream);
  const gradientState = resolveStatusVisualState({
    value: iconDatastreamValue,
    baseColor: iconColor,
    colors: iconGradientColors,
    changeColorBasedOnValue: iconGradientEnabled,
    colorGrading: iconGradientColorGrading,
  });

  const rules = Array.isArray(iconRules) ? iconRules : [];
  let ruleState = null;
  for (const rule of rules) {
    const matchedRule = getValueRuleMatch(iconDatastreamValue, [rule]);

    if (matchedRule) {
      ruleState = {
        color: matchedRule.color || iconColor,
        animation: matchedRule.animation || 'none',
        label: matchedRule.label || '',
      };
      break;
    }
  }

  const iconState = ruleState || gradientState || {
    color: iconColor,
    animation: iconDefaultAnimation,
  };
  const compact = Number(dims?.width) < 260 || Number(dims?.height) < 150;
  const normalizedTitleFontSize = Number(titleFontSize ?? labelFontSize);
  const normalizedAssetNameFontSize = Number(assetNameFontSize ?? labelFontSize);
  const normalizedDatastreamLabelFontSize = Number(datastreamLabelFontSize ?? labelFontSize);
  const normalizedValueFontSize = Number(valueFontSize);

  return (
    <div
      className={[
        'asset-status-widget',
        compact ? 'asset-status-widget--compact' : '',
        transparentBackground ? 'asset-status-widget--transparent' : '',
        draggable ? (cursorState === 'grabbing' ? 'asset-status-widget--grabbing' : 'asset-status-widget--grab') : '',
        borderEnabled ? 'asset-status-widget--bordered' : '',
      ].filter(Boolean).join(' ')}
      draggable={draggable}
      style={{
        '--asset-status-active-color': iconState.color,
        '--asset-status-border-width': `${Math.max(1, Number(borderWidth) || 1)}px`,
        ...(Number.isFinite(normalizedTitleFontSize) && normalizedTitleFontSize > 0
          ? { '--asset-status-title-font-size': `calc(${Math.min(Math.round(normalizedTitleFontSize), 80)}px * var(--font-size-multiplier, 1))` }
          : {}),
        ...(Number.isFinite(normalizedAssetNameFontSize) && normalizedAssetNameFontSize > 0
          ? { '--asset-status-asset-name-font-size': `calc(${Math.min(Math.round(normalizedAssetNameFontSize), 80)}px * var(--font-size-multiplier, 1))` }
          : {}),
        ...(Number.isFinite(normalizedDatastreamLabelFontSize) && normalizedDatastreamLabelFontSize > 0
          ? { '--asset-status-row-label-font-size': `calc(${Math.min(Math.round(normalizedDatastreamLabelFontSize), 80)}px * var(--font-size-multiplier, 1))` }
          : {}),
        ...(Number.isFinite(normalizedValueFontSize) && normalizedValueFontSize > 0
          ? { '--asset-status-value-font-size': `calc(${Math.min(Math.round(normalizedValueFontSize), 80)}px * var(--font-size-multiplier, 1))` }
          : {}),
      }}
      onMouseDown={(event) => {
        if (!draggable || isActionArea(event.target)) return;
        setCursorState('grabbing');
      }}
      onMouseUp={(event) => {
        if (!draggable || isActionArea(event.target)) return;
        setCursorState('grab');
      }}
      onMouseLeave={() => draggable && setCursorState('grab')}
      onDragStart={(event) => {
        if (isActionArea(event.target)) {
          event.preventDefault();
          return;
        }
        event.dataTransfer.setData('text/plain', buildDragData(event, id));
      }}
    >
      <div className="asset-status-widget__card">
        {title ? <div className="asset-status-widget__title">{title}</div> : null}
        <div className="asset-status-widget__panel">
          <div className="asset-status-widget__header">
            <h3>{assetName || 'Name of Assets'}</h3>
            <AssetStatusIcon color={iconState.color} animation={iconState.animation} />
          </div>
          <div className="asset-status-widget__divider" />
          <div className="asset-status-widget__rows">
            {normalizedRows.map((row) => (
              <div className="asset-status-widget__row" key={row.key}>
                <span>{row.label}</span>
                <strong>{row.valueText}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetStatusWidget;
